import React from "react";
import PageWrapper from "../components/PageWrapper";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSchool,
  faUserGraduate,
  faUsers,
  faChalkboardTeacher,
  faBook,
  faBus,
  faCreditCard,
  faChartLine,
  faLaptopCode,
  faBuilding,
  faGears,
  faCircleInfo,
  faBox,
} from "@fortawesome/free-solid-svg-icons";

/* ======================================================
   MAIN PAGE
======================================================= */
export default function Products() {
  return (
    <PageWrapper>

      {/* ======================================================
          HEADER — ULTRA PREMIUM
      ======================================================= */}
      <section className="relative overflow-hidden pt-28 pb-24">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-500/30 blur-[150px] rounded-full"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-pink-500/30 blur-[150px] rounded-full"></div>
        <div className="absolute top-20 right-1/3 w-[350px] h-[350px] bg-yellow-400/20 blur-[120px] rounded-full"></div>

        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-6xl font-extrabold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 
            bg-clip-text text-transparent drop-shadow-xl"
          >
            Our Products
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="mt-6 text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed"
          >
            Explore our next-generation, AI-powered, neon-driven school management ecosystem.
          </motion.p>
        </div>
      </section>

      {/* ======================================================
          PRINCIPAL APP
      ======================================================= */}
      <FeatureSplit
        title="Principal App"
        description="Manage your school in real-time."
        icon={faUserGraduate}
        list={[
          "Real-time student analytics",
          "Attendance approvals",
          "Fee insights & financial tracking",
          "Staff management",
          "Advanced dashboard",
        ]}
      />

      {/* ======================================================
          PARENT APP (reverse layout)
      ======================================================= */}
      <FeatureSplit
        reverse
        title="Parent App"
        description="Stay informed with instant notifications."
        icon={faUsers}
        list={[
          "Real-time performance",
          "Homework tracking",
          "Attendance alerts",
          "Fee payment portal",
          "Chat with teachers",
        ]}
      />

      {/* ======================================================
          TEACHER APP
      ======================================================= */}
      <FeatureSplit
        title="Teacher App"
        description="Boost productivity with AI-powered tools."
        icon={faSchool}
        list={[
          "Lesson planning AI",
          "Homework & assignment tools",
          "Exam mark entry",
          "Student insights",
          "Class timetable",
        ]}
      />

      {/* ======================================================
          MINI MODULE GRID
      ======================================================= */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTitle title="Explore More Modules" />

          <div className="grid md:grid-cols-3 gap-10 mt-16">
            <NeonMiniCard icon={faBook} title="Exams Automation" />
            <NeonMiniCard icon={faCreditCard} title="Fee Management" />
            <NeonMiniCard icon={faBook} title="Library System" />
          </div>
        </div>
      </section>

      {/* ======================================================
          SCHOOL ADMIN PANEL
      ======================================================= */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTitle title="School Admin Panel" />

          <div className="grid md:grid-cols-3 gap-10 mt-16">
            <NeonGlassCard title="Student Management" icon={faUserGraduate} />
            <NeonGlassCard title="Teacher Handling" icon={faChalkboardTeacher} />
            <NeonGlassCard title="Fee Management" icon={faCreditCard} />
            <NeonGlassCard title="Exam & Result" icon={faBook} />
            <NeonGlassCard title="Library System" icon={faBook} />
            <NeonGlassCard title="Transport Module" icon={faBus} />
          </div>
        </div>
      </section>

      {/* ======================================================
          SUPER ADMIN PANEL
      ======================================================= */}
      <section className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionTitle title="Super Admin Panel" />

          <div className="grid md:grid-cols-3 gap-10 mt-16">
            <NeonGlassCard title="School Onboarding" icon={faBuilding} />
            <NeonGlassCard title="Plan Management" icon={faBox} />
            <NeonGlassCard title="Billing & Finance" icon={faCreditCard} />
            <NeonGlassCard title="Analytics" icon={faChartLine} />
            <NeonGlassCard title="Support" icon={faGears} />
          </div>
        </div>
      </section>

      {/* ======================================================
          FAQ
      ======================================================= */}
      <section className="py-28 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <SectionTitle title="FAQ" />

          <div className="mt-12 space-y-4">
            <NeonFAQ q="Is this system multi tenant?" a="Yes, each school gets isolated data." />
            <NeonFAQ q="Do schools get a website?" a="Yes, dynamic website auto-generated." />
            <NeonFAQ q="Are AI features included?" a="Yes—Audio-to-Text, Analytics, Smart Notifications & more." />
          </div>
        </div>
      </section>

    </PageWrapper>
  );
}

/* ======================================================
   PREMIUM COMPONENTS
======================================================= */

function SectionTitle({ title }) {
  return (
    <h2 className="text-4xl font-extrabold text-gray-800 text-center">{title}</h2>
  );
}

/* ---------- SPLIT FEATURE SECTION ---------- */
function FeatureSplit({ title, description, list, icon, reverse = false }) {
  return (
    <section className={`py-28 ${reverse ? "bg-gray-50" : "bg-white"}`}>
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-20 items-center">

        {/* TEXT */}
        <motion.div
          initial={{ opacity: 0, x: reverse ? 40 : -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className={`${reverse && "order-2"}`}
        >
          <h2 className="text-4xl font-extrabold text-gray-800">{title}</h2>
          <p className="text-gray-600 mt-3 text-lg">{description}</p>

          <ul className="mt-8 space-y-4 text-gray-700 text-lg">
            {list.map((item, i) => (
              <li key={i}>✔ {item}</li>
            ))}
          </ul>
        </motion.div>

        {/* ICON CARD */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          className={`${reverse && "order-1"}`}
        >
          <NeonGlassCard icon={icon} />
        </motion.div>

      </div>
    </section>
  );
}

/* ---------- NEON GLASS CARD ---------- */
function NeonGlassCard({ icon, title, text }) {
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className="relative p-10 rounded-3xl bg-white/20 backdrop-blur-xl border border-white/30 
      shadow-xl flex flex-col items-center justify-center text-center"
    >
      <div className="absolute inset-0 rounded-3xl p-[2px] bg-gradient-to-r from-blue-400 via-purple-500 
      to-pink-500 opacity-60 blur-sm"></div>

      <div className="relative z-10">
        <FontAwesomeIcon
          icon={icon}
          className="text-[95px] text-indigo-600 drop-shadow-[0_0_25px_rgba(99,102,241,0.8)]"
        />

        {title && <h3 className="mt-4 text-2xl font-bold text-gray-900">{title}</h3>}
        {text && <p className="text-gray-600 mt-2 text-lg">{text}</p>}
      </div>
    </motion.div>
  );
}

/* ---------- MINI CARD ---------- */
function NeonMiniCard({ icon, title }) {
  return (
    <motion.div whileHover={{ scale: 1.05 }} className="bg-white p-10 rounded-2xl shadow-xl text-center border border-gray-200">
      <FontAwesomeIcon icon={icon} className="text-6xl text-indigo-600" />
      <h3 className="mt-4 text-xl font-bold">{title}</h3>
    </motion.div>
  );
}

/* ---------- FAQ ---------- */
function NeonFAQ({ q, a }) {
  const [open, setOpen] = React.useState(false);

  return (
    <motion.div whileHover={{ scale: 1.02 }} className="bg-white p-6 rounded-xl shadow-md border border-gray-200">
      <button onClick={() => setOpen(!open)} className="flex justify-between w-full">
        <span className="text-lg font-semibold text-gray-800">{q}</span>
        <span className="text-2xl">{open ? "−" : "+"}</span>
      </button>

      {open && <p className="mt-3 text-gray-600">{a}</p>}
    </motion.div>
  );
}